import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/** Visual variant controlling underline and weight. */
export type LinkVariant = 'inline' | 'standalone';

/**
 * Standardized hyperlink: router link, href link, or external link. Automatically
 * appends `target="_blank" rel="noopener noreferrer"` and an external-link icon
 * suffix when `external=true`. Uses `text-link` / `text-link-hover` tokens so it
 * adapts to dark mode.
 *
 * For primary page actions, use `ButtonComponent` instead.
 *
 * @example
 * <!-- Router link -->
 * <app-link routerLink="/goals">View all goals</app-link>
 *
 * @example
 * <!-- External link -->
 * <app-link href="https://blume.example/help" [external]="true">Help centre</app-link>
 *
 * @feature shared
 */
@Component({
  selector: 'app-link',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent],
  host: { class: 'inline' },
  template: `
    @if (routerLink()) {
      <a [routerLink]="routerLink()!" [class]="classes()" [attr.aria-label]="ariaLabel()">
        <ng-content />
      </a>
    } @else {
      <a
        [href]="href() ?? '#'"
        [attr.target]="external() ? '_blank' : null"
        [attr.rel]="external() ? 'noopener noreferrer' : null"
        [class]="classes()"
        [attr.aria-label]="ariaLabel()"
      >
        <ng-content />
        @if (external()) {
          <app-icon name="open-outline" size="xxs" />
          <span class="sr-only">(opens in new tab)</span>
        }
      </a>
    }
  `,
})
export class LinkComponent {
  /** Router link path. If set, takes precedence over `href`. */
  readonly routerLink = input<string | unknown[] | undefined>(undefined);

  /** Plain href. Ignored when `routerLink` is set. */
  readonly href = input<string | undefined>(undefined);

  /** When true, opens in a new tab with `rel="noopener noreferrer"` and appends an external icon. */
  readonly external = input<boolean>(false);

  /** Visual variant. @default 'inline' */
  readonly variant = input<LinkVariant>('inline');

  /** Override accessible label (e.g., when the visible text is ambiguous like "here"). */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Resolved Tailwind classes. */
  readonly classes = computed(() => {
    const base =
      'text-link hover:text-link-hover transition-colors focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)] rounded';
    const variant =
      this.variant() === 'standalone'
        ? 'inline-flex items-center gap-1 font-semibold'
        : 'underline underline-offset-2 font-semibold';
    return `${base} ${variant}`;
  });
}
